import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";

const PersonalInformationForm = ({
  personalInformation,
  handleChange,
  isLoading = false,
  isEdit,
  resetForm,
}) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: personalInformation,
  });

  const maritalOptions = [
    "Single",
    "Married",
    "Free Union",
    "Domestic Partnership",
    "Civil Partnership",
    "Civil Partnership Dissolved",
    "Civil Partner Deceased",
    "Separated",
    "Judicially Separated",
    "Divorced",
    "Widowed",
    "Pacsed",
    "Not Reported",
  ];
  useEffect(() => {
    if (resetForm) {
      reset(personalInformation);
    }
  }, [resetForm]);
  return (
    <>
      <div className="formgrid grid" data-testid="general-info-form">
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Given name(s) *</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="given_name"
              control={control}
              rules={{
                required: "Given name is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.given_name || ""}
                  onChange={(e) => {
                    handleChange("given_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter given name(s)"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.given_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.given_name && (
            <small className="p-error">{errors.given_name.message}</small>
          )}
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Other Name(s)</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="other_name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.other_name || ""}
                  onChange={(e) => {
                    handleChange("other_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter any other names, such as middle names or clan names"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.other_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.other_name && (
            <small className="p-error">{errors.other_name.message}</small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Surname(s) *</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: "Surname is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.last_name || ""}
                  onChange={(e) => {
                    handleChange("last_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter surname(s)."
                  className={`input-field appearance-none outline-none w-full ${
                    errors.last_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.last_name && (
            <small className="p-error">{errors.last_name.message}</small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Preferred name(s)</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="preferred_name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.preferred_name || ""}
                  onChange={(e) => {
                    handleChange("preferred_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Preferred name or nickname"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.preferred_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.preferred_name && (
            <small className="p-error">{errors.preferred_name.message}</small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Tax identification number</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="tax_identification_no"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.tax_identification_no || ""}
                  onChange={(e) => {
                    handleChange("tax_identification_no", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter Tax Identification Number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.tax_identification_no ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.tax_identification_no && (
            <small className="p-error">
              {errors.tax_identification_no.message}
            </small>
          )}
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">National ID </label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="national_id"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.national_id || ""}
                  onChange={(e) => {
                    handleChange("national_id", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter National ID"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.national_id ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.national_id && (
            <small className="p-error">{errors.national_id.message}</small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Social Security Number</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="social_security_number"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.social_security_number || ""}
                  onChange={(e) => {
                    handleChange("social_security_number", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter SSN"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.social_security_number ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.social_security_number && (
            <small className="p-error">
              {errors.social_security_number.message}
            </small>
          )}
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Marital Status </label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="marital_status"
              control={control}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={personalInformation?.marital_status}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("marital_status", newValue);
                      field.onChange(newValue);
                    }}
                    options={maritalOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select status"
                    className={`w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    disabled={!isEdit}
                  />
                );
              }}
            />
          )}
          {errors.marital_status && (
            <small className="p-error">{errors.marital_status.message}</small>
          )}
        </div>
        <div className="mb-2 col-12 md:col-12 sm:col-12">
          <p>
            <b>Contact Information</b>
          </p>
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label">Personal email *</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="personal_email"
              control={control}
              rules={{
                required: "Personal email is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.personal_email || ""}
                  onChange={(e) => {
                    handleChange("personal_email", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter personal email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.personal_email ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-name"
                />
              )}
            />
          )}
          {errors.personal_email && (
            <small className="p-error">{errors.personal_email.message}</small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label">Work email *</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="work_email"
              control={control}
              rules={{
                required: "Work email  is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.work_email || ""}
                  onChange={(e) => {
                    handleChange("work_email", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter work email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.work_email ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-email"
                />
              )}
            />
          )}

          {errors.work_email && (
            <small className="p-error">{errors.work_email.message}</small>
          )}
        </div>
        <div className="col-12 md:col-12 sm:col-12">
          <label className="input-label">Phone number*</label>
        </div>
        <div className="pt-2 col-12 md:col-3 sm:col-4 ">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="phone_code"
              control={control}
              rules={{
                required: "Country code is required.",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Country code must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.phone_code || ""}
                  onChange={(e) => {
                    handleChange("phone_code", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Country Code"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone_code ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-phone-code"
                />
              )}
            />
          )}

          {errors.phone_code && (
            <small className="p-error">{errors.phone_code.message}</small>
          )}
        </div>
        <div className="pt-2  col-12 md:col-9 sm:col-8">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              rules={{
                required: "Phone number is required.",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone number must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={personalInformation?.phone || ""}
                  onChange={(e) => {
                    handleChange("phone", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter Phone Number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-phone"
                />
              )}
            />
          )}

          {errors.phone && (
            <small className="p-error">{errors.phone.message}</small>
          )}
        </div>
      </div>
    </>
  );
};
PersonalInformationForm.propTypes = {
  personalInformation: PropTypes.object,
  handleChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};
export default PersonalInformationForm;
